import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { CgSpinner } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import TableWithSearchAndPagination from "../components/TableWithSearchAndPagination";

Modal.setAppElement("#root"); // For accessibility

const BatchesPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newBatch, setNewBatch] = useState({
    batch_number: "",
    exp_date: "",
    mfg_date: "",
    product_id: "",
    extra_fields: {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extraFields, setExtraFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [batchesPerPage] = useState(10);
  const [companyCashbackEnabled, setCompanyCashbackEnabled] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    name: "",
    role: "",
    lastLogin: "",
    companyName: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get("/protected");
        const { name, role, last_login, company_name, company_id } =
          userRes.data;
        setUserData({
          name,
          role,
          lastLogin: last_login,
          companyName: company_name,
        });
        if (role === "salesman") {
          navigate("/seller");
        }

        // Check if the company has cashback_enabled
        const companyRes = await axios.get(`/company/${company_id}`);
        setCompanyCashbackEnabled(companyRes.data.cashback_enabled);

        const productsRes = await axios.get("/products");
        setProducts(productsRes.data);

        const batchesRes = await axios.get("/batches");
        setBatches(batchesRes.data);
        setFilteredBatches(batchesRes.data);

        const extraFieldsRes = await axios.get("/company-extra-fields");
        setExtraFields(extraFieldsRes.data.extra_field_batch);
      } catch (error) {
        console.error("Error fetching data:", error);
        navigate("/login");
      }
      setIsLoading(false);
    };
    fetchData();
  }, [navigate]);

  useEffect(() => {
    const filtered = batches.filter((batch) =>
      batch.batch_number.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBatches(filtered);
  }, [searchTerm, batches]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBatch({ ...newBatch, [name]: value });
  };

  const handleExtraFieldChange = (e) => {
    const { name, value } = e.target;
    setNewBatch((prevBatch) => ({
      ...prevBatch,
      extra_fields: { ...prevBatch.extra_fields, [name]: value },
    }));
  };

  const handleAddBatch = async () => {
    setIsSubmitting(true);
    try {
      await axios.post("/add-batch", newBatch);
      setNewBatch({
        batch_number: "",
        exp_date: "",
        mfg_date: "",
        product_id: "",
        extra_fields: {},
      });
      setShowModal(false);
      setIsSubmitting(false);
      alert("Batch Added Successfully");
      // Refetch batches after adding
      const response = await axios.get("/batches");
      setBatches(response.data);
    } catch (error) {
      console.error("Error adding batch:", error);
      setIsSubmitting(false);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const indexOfLastBatch = (currentPage + 1) * batchesPerPage;
  const indexOfFirstBatch = indexOfLastBatch - batchesPerPage;
  const currentBatches = filteredBatches.slice(
    indexOfFirstBatch,
    indexOfLastBatch
  );

  // Calculate the total page count
  const totalPages = Math.ceil(filteredBatches.length / batchesPerPage);

  // Updated Table Headers
  const tableHeaders = [
    "Batch Number",
    "Product Alias",
    "MRP",
    "Manufacturing Date",
    "Expiration Date",
    "Plant Name",
  ];

  // Table Rows
  const tableRows = currentBatches.map((batch) => [
    batch.batch_number,
    batch.product_alias.product_alias, // Access product_alias from nested object
    batch.mrp,
    batch.mfg_date,
    batch.exp_date,
    batch.plant_name,
  ]);
  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar userData={userData} />
      <div className="flex-1">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <CgSpinner className="animate-spin text-4xl" />
          </div>
        ) : (
          <>
            <Header userData={userData} />
            <div className="p-6">
              <TableWithSearchAndPagination
                tableHeaders={tableHeaders}
                tableRows={tableRows}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                buttonText="Add New Batch"
                onButtonClick={() => setShowModal(true)}
                pageCount={Math.ceil(filteredBatches.length / batchesPerPage)}
                onPageChange={handlePageClick}
                currentPage={currentPage}
              />

              <Modal
                isOpen={showModal}
                onRequestClose={() => setShowModal(false)}
                contentLabel="Add New Batch"
                className="bg-white p-6 rounded shadow-lg w-1/2 mx-auto mt-10"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50"
                style={{
                  content: {
                    maxHeight: "95vh",
                    overflowY: "auto",
                  },
                }}
              >
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                  &times;
                </button>
                <h2 className="text-xl font-bold mb-2">Add New Batch</h2>

                {/* Input fields arranged in 2 columns */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Batch Number
                    </label>
                    <input
                      type="text"
                      name="batch_number"
                      value={newBatch.batch_number}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Max Retail Price
                    </label>
                    <input
                      type="number"
                      name="mrp"
                      value={newBatch.mrp}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      name="exp_date"
                      value={newBatch.exp_date}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Manufacturing Date
                    </label>
                    <input
                      type="date"
                      name="mfg_date"
                      value={newBatch.mfg_date}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  {/* Product & Cashback Fields in One Row */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Product
                    </label>
                    <select
                      name="product_id"
                      value={newBatch.product_id}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select Product</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {companyCashbackEnabled && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Cashback
                      </label>
                      <input
                        type="number"
                        name="cashback_amount"
                        value={newBatch.cashback_amount}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  )}
                </div>

                {/* Render extra fields dynamically */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {extraFields.map((field, index) => (
                    <div key={index}>
                      <label className="block text-sm font-medium mb-1">
                        {field}
                      </label>
                      <input
                        type="text"
                        name={field}
                        value={newBatch.extra_fields[field] || ""}
                        onChange={handleExtraFieldChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  ))}
                </div>

                {/* Add Batch Button */}
                <div className="mt-6">
                  <button
                    onClick={handleAddBatch}
                    className={`w-full bg-teal-600 text-white font-bold px-4 py-3 rounded text-lg ${
                      isSubmitting
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-teal-700"
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Adding..." : "ADD BATCH"}
                  </button>
                </div>
              </Modal>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BatchesPage;
